import { useEffect, useMemo, useState } from 'react';
import {
  Settings, LayoutDashboard, Users, Box, AppWindow, Server, RefreshCw,
  ShieldCheck, CheckCircle2, AlertTriangle, ClipboardList, FileBarChart,
  Download, FileText, FileSpreadsheet, X, Search, UserCheck, AlertCircle,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import CostManagementPanel from '../components/CostManagementPanel';

const TABS = [
  { key: 'dashboard',   label: 'Dashboard',           Icon: LayoutDashboard },
  { key: 'users',       label: 'Users',               Icon: Users },
  { key: 'sps',         label: 'Service Accounts',    Icon: Box },
  { key: 'apps',        label: 'Applications',        Icon: AppWindow },
  { key: 'mi',          label: 'Managed Identities',  Icon: Server },
  { key: 'reviews',     label: 'Access Reviews',      Icon: ClipboardList },
  { key: 'reports',     label: 'Reports',             Icon: FileBarChart },
  { key: 'cost',        label: 'Cost Management',     Icon: Wallet },
  { key: 'settings',    label: 'Entra Settings',      Icon: Settings },
];

export default function EntraGovernance() {
  const [tab, setTab] = useState('dashboard');
  const [bump, setBump] = useState(0);
  const [detail, setDetail] = useState(null); // {kind, id}

  const refresh = () => setBump((b) => b + 1);

  return (
    <div className="space-y-5" data-testid="entra-governance-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
          <a href="/security" className="hover:text-[#3D4A8E]" data-testid="entra-breadcrumb-security">Security &amp; Governance</a> · Microsoft Entra
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1 inline-flex items-center gap-2">
          <ShieldCheck className="w-7 h-7 text-[#3D4A8E]" /> Entra Governance
        </h1>
        <p className="text-slate-500 mt-1">Read-only Microsoft Entra (Azure AD) visibility · ownership tracking · access reviews · cost.</p>
      </div>

      <div className="flex flex-wrap gap-1 border-b-2 border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5 border-b-2 -mb-0.5 transition-colors whitespace-nowrap ${
              tab === t.key ? 'border-[#3D4A8E] text-[#3D4A8E]' : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <t.Icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab bump={bump} onJump={(t) => setTab(t)} />}
      {tab === 'users'     && <UsersTab onOpen={(id) => setDetail({ kind: 'user', id })} />}
      {tab === 'sps'       && <CollectionTab kind="service-principals" gKind="sp"  title="Service Accounts" onOpen={(id) => setDetail({ kind: 'sp',  id })} />}
      {tab === 'apps'      && <CollectionTab kind="applications"       gKind="app" title="Applications"     onOpen={(id) => setDetail({ kind: 'app', id })} />}
      {tab === 'mi'        && <CollectionTab kind="managed-identities" gKind="mi"  title="Managed Identities" onOpen={(id) => setDetail({ kind: 'mi',  id })} />}
      {tab === 'reviews'   && <ReviewsTab onOpen={(kind, id) => setDetail({ kind, id })} />}
      {tab === 'reports'   && <ReportsTab />}
      {tab === 'cost'      && <CostManagementPanel provider="entra" />}
      {tab === 'settings'  && <SettingsTab onSynced={refresh} />}

      {detail && <DetailDrawer entity={detail} onClose={() => setDetail(null)} onSaved={refresh} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
function DashboardTab({ bump, onJump }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState('');

  const reload = async () => {
    try { const { data } = await api.get('/governance/entra/dashboard'); setD(data); }
    catch { setD(null); }
  };

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/governance/entra/dashboard'); setD(data); }
      catch { setD(null); }
    })();
  }, [bump]);

  const runChecks = async () => {
    setBusy('checks');
    try {
      const { data } = await api.post('/governance/entra/run-checks');
      const s = data.summary || {};
      toast.success(`Governance checks: ${s.tickets_created || 0} ticket(s) created (cap ${s.tickets_skipped_cap ? 'reached' : 'OK'})`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Run failed'); }
    finally { setBusy(''); }
  };

  if (!d) return <div className="text-sm text-slate-500">Loading…</div>;

  const cards = [
    { key: 'users',     label: 'Entra Users',         value: d.users_total,               jump: 'users',  accent: 'text-indigo-600 bg-indigo-50' },
    { key: 'sps',       label: 'Service Accounts',    value: d.service_principals_total,  jump: 'sps',    accent: 'text-blue-600 bg-blue-50' },
    { key: 'apps',      label: 'Applications',        value: d.applications_total,        jump: 'apps',   accent: 'text-emerald-600 bg-emerald-50' },
    { key: 'mi',        label: 'Managed Identities',  value: d.managed_identities_total,  jump: 'mi',     accent: 'text-purple-600 bg-purple-50' },
    { key: 'noowner',   label: 'No HRMS owner',       value: d.no_owner,                  jump: 'sps',    accent: 'text-amber-600 bg-amber-50' },
    { key: 'highrisk',  label: 'High risk',           value: d.high_risk,                 jump: 'users',  accent: 'text-rose-600 bg-rose-50' },
    { key: 'unmatched', label: 'Unmatched users',     value: d.unmatched_hrms,            jump: 'users',  accent: 'text-slate-600 bg-slate-100' },
    { key: 'stale',     label: 'Stale users',         value: d.stale_users,               jump: 'users',  accent: 'text-orange-600 bg-orange-50' },
    { key: 'overdue',   label: 'Overdue reviews',     value: d.overdue_reviews,           jump: 'reviews', accent: 'text-fuchsia-600 bg-fuchsia-50' },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="entra-kpi-strip">
        {cards.map((c) => (
          <button
            key={c.key}
            onClick={() => onJump?.(c.jump)}
            data-testid={`entra-kpi-${c.key}`}
            className="bg-white border border-slate-200 rounded-2xl p-4 flex items-start justify-between text-left hover:border-[#3D4A8E]/40 hover:shadow-sm transition-all"
          >
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{c.label}</div>
              <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{c.value ?? '—'}</div>
            </div>
            <div className={`w-9 h-9 rounded-xl ${c.accent} flex items-center justify-center`}>
              <ShieldCheck className="w-4 h-4" strokeWidth={1.8} />
            </div>
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-center gap-3 justify-between">
        <div className="text-xs text-slate-500" data-testid="entra-last-sync">
          Last sync: <b>{d.last_sync_at ? new Date(d.last_sync_at).toLocaleString() : 'never'}</b>
          {d.last_sync_status && <> · status <b>{d.last_sync_status}</b></>}
          <span className="mx-2">·</span>
          Last checks: <b>{d.last_checks_at ? new Date(d.last_checks_at).toLocaleString() : 'never'}</b>
        </div>
        <Button
          onClick={runChecks}
          disabled={busy === 'checks'}
          variant="outline"
          className="rounded-full"
          data-testid="entra-run-checks-btn"
        >
          <ShieldCheck className={`w-3.5 h-3.5 mr-1.5 ${busy === 'checks' ? 'animate-spin' : ''}`} />
          {busy === 'checks' ? 'Running…' : 'Run governance checks'}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Users tab
// ---------------------------------------------------------------------------
function UsersTab({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [risk, setRisk] = useState('');
  const [matched, setMatched] = useState('');

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      const qs = new URLSearchParams();
      if (q) qs.set('q', q);
      if (risk) qs.set('risk', risk);
      if (matched === 'true') qs.set('matched', 'true');
      if (matched === 'false') qs.set('matched', 'false');
      try {
        const { data } = await api.get(`/governance/entra/users?${qs.toString()}`);
        if (!cancelled) setRows(data || []);
      } catch { if (!cancelled) setRows([]); }
    }, 200);
    return () => { clearTimeout(t); cancelled = true; };
  }, [q, risk, matched]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name / email / dept / UPN" className="rounded-xl pl-9" data-testid="entra-user-search" />
        </div>
        <select value={risk} onChange={(e) => setRisk(e.target.value)} className="rounded-xl border border-slate-200 px-3 text-sm" data-testid="entra-user-risk-filter">
          <option value="">All risks</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
        </select>
        <select value={matched} onChange={(e) => setMatched(e.target.value)} className="rounded-xl border border-slate-200 px-3 text-sm" data-testid="entra-user-match-filter">
          <option value="">All</option><option value="true">Matched only</option><option value="false">Unmatched only</option>
        </select>
        <div className="ml-auto text-xs text-slate-500 self-center"><b>{rows.length}</b> result(s)</div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">UPN</th>
              <th className="text-left px-4 py-3">Dept</th>
              <th className="text-left px-4 py-3">Enabled</th>
              <th className="text-left px-4 py-3">HRMS Match</th>
              <th className="text-left px-4 py-3">Risk</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => onOpen?.(u.id)} data-testid={`entra-user-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{u.display_name}</div>
                  <div className="text-[11px] text-slate-500">{u.email}</div>
                </td>
                <td className="px-4 py-3 text-xs font-mono">{u.upn}</td>
                <td className="px-4 py-3">{u.department || '—'}</td>
                <td className="px-4 py-3">{u.account_enabled ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-rose-600" />}</td>
                <td className="px-4 py-3 text-xs">{u.hrms_employee_name
                  ? <span className="text-emerald-700">{u.hrms_employee_name}</span>
                  : <span className="text-rose-600">Unmatched</span>}</td>
                <td className="px-4 py-3"><RiskBadge level={u.risk_level} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-10 text-sm">No users. Run a sync from the Settings tab.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic SP / App / MI tab
// ---------------------------------------------------------------------------
function CollectionTab({ kind, gKind, title, onOpen }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [noOwner, setNoOwner] = useState(false);
  const [risk, setRisk] = useState('');

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      const qs = new URLSearchParams();
      if (q) qs.set('q', q);
      if (noOwner) qs.set('no_owner', 'true');
      if (risk) qs.set('risk', risk);
      try {
        const { data } = await api.get(`/governance/entra/${kind}?${qs.toString()}`);
        if (!cancelled) setRows(data || []);
      } catch { if (!cancelled) setRows([]); }
    }, 200);
    return () => { clearTimeout(t); cancelled = true; };
  }, [kind, q, noOwner, risk]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${title.toLowerCase()}…`} className="rounded-xl pl-9" data-testid={`entra-${gKind}-search-input`} />
        </div>
        <select value={risk} onChange={(e) => setRisk(e.target.value)} className="rounded-xl border border-slate-200 px-3 text-sm" data-testid={`entra-${gKind}-risk-filter`}>
          <option value="">All risks</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 px-3">
          <input type="checkbox" checked={noOwner} onChange={(e) => setNoOwner(e.target.checked)} data-testid={`entra-${gKind}-noowner`} />
          No owner only
        </label>
        <div className="ml-auto text-xs text-slate-500 self-center"><b>{rows.length}</b> result(s)</div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">{title}</th>
              <th className="text-left px-4 py-3">Owners</th>
              <th className="text-left px-4 py-3">Permissions</th>
              <th className="text-left px-4 py-3">Review</th>
              <th className="text-left px-4 py-3">Risk</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => onOpen?.(r.id)} data-testid={`entra-${gKind}-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{r.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{r.app_id || r.id}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  {(r.entra_owners || []).length === 0
                    ? <span className="text-rose-600">No Entra owner</span>
                    : (r.entra_owners || []).map((o) => o.name).join(', ')}
                </td>
                <td className="px-4 py-3 text-xs">{r.permission_count ?? 0}</td>
                <td className="px-4 py-3 text-xs">
                  {r.review_overdue
                    ? <span className="text-rose-600">Overdue</span>
                    : <span className="text-slate-500">—</span>}
                </td>
                <td className="px-4 py-3"><RiskBadge level={r.risk_level} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-10 text-sm">No data. Run a sync.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reviews tab
// ---------------------------------------------------------------------------
function ReviewsTab({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const qs = filter === 'all' ? '' : `?status=${filter}`;
      try {
        const { data } = await api.get(`/governance/entra/reviews${qs}`);
        if (!cancelled) setRows(data || []);
      } catch { if (!cancelled) setRows([]); }
    })();
    return () => { cancelled = true; };
  }, [filter]);

  const filters = [
    { k: 'all',       label: 'All' },
    { k: 'overdue',   label: 'Overdue' },
    { k: 'upcoming',  label: 'Upcoming (30d)' },
    { k: 'completed', label: 'Completed' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {filters.map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`entra-review-filter-${f.k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              filter === f.k ? 'bg-[#3D4A8E] text-white border-[#3D4A8E]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto text-xs text-slate-500 self-center"><b>{rows.length}</b> review item(s)</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Resource</th>
              <th className="text-left px-4 py-3">Owner</th>
              <th className="text-left px-4 py-3">Last reviewed</th>
              <th className="text-left px-4 py-3">Next due</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.kind}:${r.id}`} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => onOpen?.(r.kind, r.id)} data-testid={`entra-review-${r.kind}-${r.id}`}>
                <td className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">{r.kind}</td>
                <td className="px-4 py-3 font-semibold text-slate-900">{r.name}</td>
                <td className="px-4 py-3 text-xs">{r.primary_owner_name || <span className="text-rose-600">No owner</span>}</td>
                <td className="px-4 py-3 text-xs">{r.last_reviewed_date ? new Date(r.last_reviewed_date).toLocaleDateString() : 'never'}</td>
                <td className="px-4 py-3 text-xs">{r.next_review_date ? new Date(r.next_review_date).toLocaleDateString() : '—'}</td>
                <td className="px-4 py-3">
                  {r.overdue && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">Overdue</span>}
                  {r.upcoming && !r.overdue && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Due soon</span>}
                  {r.completed && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Completed</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-10 text-sm">No review items yet — assign owners + set review dates from a resource detail to populate this list.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reports tab
// ---------------------------------------------------------------------------
function ReportsTab() {
  const [reports, setReports] = useState([]);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/governance/entra/reports/list'); setReports(data || []); }
      catch { setReports([]); }
    })();
  }, []);
  const token = localStorage.getItem('hrms_token');

  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">Download read-only governance reports for compliance, ISO 27001, or quarterly access reviews. Native browser downloads — no popup blockers.</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {reports.map((r) => (
          <div key={r.kind} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`entra-report-${r.kind}`}>
            <div className="font-semibold text-slate-900 text-sm">{r.label}</div>
            <div className="text-[11px] text-slate-500 font-mono mt-0.5">{r.kind}</div>
            <div className="flex gap-1.5 mt-3 flex-wrap">
              <a href={`/api/governance/entra/reports/${r.kind}?format=pdf&auth=${token}`}
                 className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100"
                 data-testid={`entra-report-pdf-${r.kind}`}>
                <FileText className="w-3 h-3" /> PDF
              </a>
              <a href={`/api/governance/entra/reports/${r.kind}?format=xlsx&auth=${token}`}
                 className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                 data-testid={`entra-report-xlsx-${r.kind}`}>
                <FileSpreadsheet className="w-3 h-3" /> Excel
              </a>
              <a href={`/api/governance/entra/reports/${r.kind}?format=csv&auth=${token}`}
                 className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100"
                 data-testid={`entra-report-csv-${r.kind}`}>
                <Download className="w-3 h-3" /> CSV
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer (user / sp / app / mi)
// ---------------------------------------------------------------------------
function DetailDrawer({ entity, onClose, onSaved }) {
  const { kind, id } = entity;
  const [obj, setObj] = useState(null);
  const [g, setG] = useState({});
  const [busy, setBusy] = useState(false);

  // owner picker state
  const [q, setQ] = useState('');
  const [options, setOptions] = useState([]);
  const [pickingFor, setPickingFor] = useState(null); // 'primary' | 'backup' | null

  const endpointMap = useMemo(() => ({
    user: 'users',
    sp:   'service-principals',
    app:  'applications',
    mi:   'managed-identities',
  }), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (kind === 'mi') {
          const { data } = await api.get('/governance/entra/managed-identities');
          if (cancelled) return;
          const row = (data || []).find((x) => x.id === id);
          if (row) { setObj({ ...row, governance: {} }); setG({}); }
        } else {
          const { data } = await api.get(`/governance/entra/${endpointMap[kind]}/${id}`);
          if (cancelled) return;
          setObj(data); setG(data.governance || {});
        }
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Failed to load details');
      }
    })();
    return () => { cancelled = true; };
  }, [kind, id, endpointMap]);

  const reloadObj = async () => {
    try {
      if (kind === 'mi') {
        const { data } = await api.get('/governance/entra/managed-identities');
        const row = (data || []).find((x) => x.id === id);
        if (row) { setObj({ ...row, governance: {} }); }
      } else {
        const { data } = await api.get(`/governance/entra/${endpointMap[kind]}/${id}`);
        setObj(data); setG(data.governance || {});
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!pickingFor) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/governance/entra/hrms-users${q ? `?q=${encodeURIComponent(q)}` : ''}`);
        if (!cancelled) setOptions(data || []);
      } catch { if (!cancelled) setOptions([]); }
    }, 150);
    return () => { clearTimeout(t); cancelled = true; };
  }, [q, pickingFor]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        primary_owner_id: g.primary_owner_id || null,
        backup_owner_id: g.backup_owner_id || null,
        business_justification: g.business_justification || null,
        review_status: g.review_status || null,
        next_review_date: g.next_review_date || null,
        review_notes: g.review_notes || null,
      };
      await api.patch(`/governance/entra/governance/${kind}/${id}`, payload);
      toast.success('Governance saved');
      onSaved?.();
      await reloadObj();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const setPrimary = (u) => { setG({ ...g, primary_owner_id: u.id, primary_owner_name: u.name }); setPickingFor(null); setQ(''); };
  const setBackup  = (u) => { setG({ ...g, backup_owner_id: u.id, backup_owner_name: u.name }); setPickingFor(null); setQ(''); };
  const clearOwner = (which) => setG({ ...g, [`${which}_owner_id`]: null, [`${which}_owner_name`]: null });

  if (!obj) return null;

  const name  = obj.display_name || obj.name || id;
  const upn   = obj.upn || obj.app_id || '';
  const kindLabel = {
    user: 'Entra User', sp: 'Service Account', app: 'Application', mi: 'Managed Identity',
  }[kind];

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="entra-detail-drawer">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-white border-l border-slate-200 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-start justify-between gap-2">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">{kindLabel}</div>
            <div className="font-heading text-lg font-bold text-slate-900 mt-0.5">{name}</div>
            {upn && <div className="text-[11px] text-slate-500 font-mono">{upn}</div>}
            <div className="mt-2"><RiskBadge level={obj.risk_level} /></div>
          </div>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-slate-100" data-testid="entra-drawer-close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Entra-side facts */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 text-xs space-y-1">
            {kind === 'user' && (
              <>
                <Row label="Email">{obj.email || '—'}</Row>
                <Row label="Department">{obj.department || '—'}</Row>
                <Row label="Job title">{obj.job_title || '—'}</Row>
                <Row label="Account enabled">{obj.account_enabled ? 'Yes' : 'No'}</Row>
                <Row label="Created">{(obj.created_at || '').slice(0,10) || '—'}</Row>
                <Row label="Last password change">{(obj.last_password_change || '').slice(0,10) || '—'}</Row>
                <Row label="Last sign-in">{(obj.last_signin_at || '').slice(0,19).replace('T',' ') || 'never'}</Row>
                <Row label="Stale">{obj.is_stale ? <span className="text-rose-600">Yes</span> : 'No'}</Row>
                <Row label="HRMS employee">
                  {obj.hrms_employee_name ? (
                    <span className="text-emerald-700">{obj.hrms_employee_name} <span className="text-slate-400">({obj.hrms_employee_code})</span></span>
                  ) : <span className="text-rose-600">Unmatched</span>}
                </Row>
              </>
            )}
            {kind !== 'user' && (
              <>
                <Row label="Description">{obj.description || '—'}</Row>
                <Row label="Type">{obj.type || (kind === 'app' ? 'Application' : kindLabel)}</Row>
                <Row label="Entra owners">{(obj.entra_owners || []).length === 0 ? <span className="text-rose-600">None</span> : (obj.entra_owners || []).map((o) => o.name || o.upn).join(', ')}</Row>
                <Row label="Permissions">{obj.permission_count ?? 0}</Row>
                <Row label="Created">{(obj.created_at || '').slice(0,10) || '—'}</Row>
              </>
            )}
          </div>

          {/* HRMS Owners */}
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">HRMS Ownership</div>
            <OwnerRow
              label="Primary owner"
              owner={g.primary_owner_name}
              onPick={() => setPickingFor('primary')}
              onClear={() => clearOwner('primary')}
              testId="entra-primary-owner"
            />
            <OwnerRow
              label="Backup owner"
              owner={g.backup_owner_name}
              onPick={() => setPickingFor('backup')}
              onClear={() => clearOwner('backup')}
              testId="entra-backup-owner"
            />
            <div className="mt-2">
              <label className="text-[11px] font-semibold text-slate-500">Business justification</label>
              <Textarea
                rows={2}
                value={g.business_justification || ''}
                onChange={(e) => setG({ ...g, business_justification: e.target.value })}
                placeholder="Why does this resource exist? Who depends on it?"
                className="rounded-xl mt-1 text-sm"
                data-testid="entra-business-justification"
              />
            </div>
          </div>

          {/* Access Review */}
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Access Review</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Status</label>
                <select
                  value={g.review_status || ''}
                  onChange={(e) => setG({ ...g, review_status: e.target.value || null })}
                  className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  data-testid="entra-review-status"
                >
                  <option value="">— Select —</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Next review date</label>
                <Input
                  type="date"
                  value={(g.next_review_date || '').slice(0,10)}
                  onChange={(e) => setG({ ...g, next_review_date: e.target.value })}
                  className="rounded-xl mt-1 text-sm"
                  data-testid="entra-next-review-date"
                />
              </div>
            </div>
            <div className="mt-2 text-[11px] text-slate-500">
              Last reviewed: <b>{g.last_reviewed_date ? new Date(g.last_reviewed_date).toLocaleString() : 'never'}</b>
            </div>
            <div className="mt-2">
              <label className="text-[11px] font-semibold text-slate-500">Notes</label>
              <Textarea
                rows={2}
                value={g.review_notes || ''}
                onChange={(e) => setG({ ...g, review_notes: e.target.value })}
                placeholder="Reviewer notes for this cycle"
                className="rounded-xl mt-1 text-sm"
                data-testid="entra-review-notes"
              />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-3 flex justify-end gap-2">
          <Button onClick={onClose} variant="outline" className="rounded-full" data-testid="entra-drawer-cancel">Close</Button>
          <Button onClick={save} disabled={busy} className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="entra-drawer-save">
            {busy ? 'Saving…' : 'Save governance'}
          </Button>
        </div>

        {/* Owner picker overlay */}
        {pickingFor && (
          <div className="fixed inset-0 z-[60] bg-slate-900/40 flex items-start justify-center pt-24" onClick={() => setPickingFor(null)}>
            <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
                Pick {pickingFor === 'primary' ? 'primary' : 'backup'} owner
              </div>
              <Input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name / email / employee ID"
                className="rounded-xl"
                data-testid="entra-owner-picker-search"
              />
              <div className="mt-2 max-h-72 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-xl">
                {options.length === 0 && <div className="text-xs text-slate-400 p-3">Type to search…</div>}
                {options.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => (pickingFor === 'primary' ? setPrimary(u) : setBackup(u))}
                    data-testid={`entra-owner-option-${u.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors"
                  >
                    <div className="font-semibold text-sm text-slate-900">{u.name}</div>
                    <div className="text-[11px] text-slate-500">{u.email} · {u.department || '—'} {u.employee_id ? `· ${u.employee_id}` : ''}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 font-medium text-right break-all">{children}</span>
    </div>
  );
}

function OwnerRow({ label, owner, onPick, onClear, testId }) {
  return (
    <div className="flex items-center gap-2 py-1.5">
      <div className="text-xs text-slate-500 w-28">{label}</div>
      <div className="flex-1">
        {owner ? (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs" data-testid={`${testId}-chip`}>
            <UserCheck className="w-3 h-3" /> {owner}
            <button onClick={onClear} className="ml-1 hover:text-rose-600" data-testid={`${testId}-clear`}><X className="w-3 h-3" /></button>
          </span>
        ) : (
          <span className="text-xs text-slate-400 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> No owner</span>
        )}
      </div>
      <Button onClick={onPick} variant="outline" size="sm" className="rounded-full text-xs h-7" data-testid={`${testId}-pick`}>
        {owner ? 'Change' : 'Assign'}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings tab
// ---------------------------------------------------------------------------
function SettingsTab({ onSynced }) {
  const [form, setForm] = useState({
    tenant_id: '', client_id: '', client_secret: '',
    stale_days: 90, review_period_days: 180,
  });
  const [info, setInfo] = useState({
    client_secret_set: false, last_sync_at: null, last_sync_status: null,
    stale_days: 90, review_period_days: 180,
  });
  const [busy, setBusy] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/governance/entra/settings');
        setInfo(data);
        setForm((f) => ({
          ...f,
          tenant_id: data.tenant_id || '',
          client_id: data.client_id || '',
          stale_days: data.stale_days ?? 90,
          review_period_days: data.review_period_days ?? 180,
        }));
      } catch { /* ignore */ }
    })();
  }, []);

  const save = async () => {
    setBusy('save');
    try {
      const payload = {
        tenant_id: form.tenant_id, client_id: form.client_id,
        stale_days: Number(form.stale_days) || 90,
        review_period_days: Number(form.review_period_days) || 180,
      };
      if (form.client_secret) payload.client_secret = form.client_secret;
      await api.put('/governance/entra/settings', payload);
      toast.success('Settings saved');
      setForm((f) => ({ ...f, client_secret: '' }));
      setInfo((i) => ({ ...i, client_secret_set: form.client_secret ? true : i.client_secret_set }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(''); }
  };

  const testConn = async () => {
    setBusy('test');
    try {
      const { data } = await api.post('/governance/entra/test-connection');
      toast.success(`Connected to ${data.tenant_name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test failed'); }
    finally { setBusy(''); }
  };

  const sync = async () => {
    setBusy('sync');
    try {
      const { data } = await api.post('/governance/entra/sync');
      const s = data.summary || {};
      toast.success(`Sync complete: ${s.users || 0} users · ${s.service_principals || 0} SPs · ${s.applications || 0} apps · ${s.managed_identities || 0} MIs`);
      onSynced?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Sync failed'); }
    finally { setBusy(''); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl space-y-4">
      <div>
        <label className="text-xs font-semibold text-slate-600">Tenant ID *</label>
        <Input value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
               className="rounded-xl mt-1 font-mono" placeholder="00000000-0000-0000-0000-000000000000" data-testid="entra-tenant-id" />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">Client ID *</label>
        <Input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
               className="rounded-xl mt-1 font-mono" data-testid="entra-client-id" />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">
          Client Secret {info.client_secret_set && <span className="text-emerald-700 font-normal">· already stored (leave blank to keep)</span>}
        </label>
        <Input type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
               className="rounded-xl mt-1 font-mono" placeholder={info.client_secret_set ? '••••••••••••••' : 'Paste secret here'} data-testid="entra-client-secret" />
        <div className="text-[11px] text-slate-500 mt-1">Encrypted with Fernet before persisting. Never returned in API responses.</div>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Governance thresholds</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Stale account (days without sign-in)</label>
            <Input type="number" min={1} value={form.stale_days}
                   onChange={(e) => setForm({ ...form, stale_days: e.target.value })}
                   className="rounded-xl mt-1" data-testid="entra-stale-days" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Access review cycle (days)</label>
            <Input type="number" min={1} value={form.review_period_days}
                   onChange={(e) => setForm({ ...form, review_period_days: e.target.value })}
                   className="rounded-xl mt-1" data-testid="entra-review-period-days" />
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap pt-2">
        <Button onClick={save} disabled={busy === 'save' || !form.tenant_id || !form.client_id}
                className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="entra-save-btn">
          {busy === 'save' ? 'Saving…' : 'Save settings'}
        </Button>
        <Button onClick={testConn} disabled={busy === 'test'} variant="outline" className="rounded-full" data-testid="entra-test-btn">
          {busy === 'test' ? 'Testing…' : 'Test connection'}
        </Button>
        <Button onClick={sync} disabled={busy === 'sync'} variant="outline" className="rounded-full" data-testid="entra-sync-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy === 'sync' ? 'animate-spin' : ''}`} />
          {busy === 'sync' ? 'Syncing…' : 'Sync now'}
        </Button>
      </div>

      <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
        Last sync: <b>{info.last_sync_at ? new Date(info.last_sync_at).toLocaleString() : 'never'}</b>
        {info.last_sync_status && <> · status <b>{info.last_sync_status}</b></>}
      </div>
    </div>
  );
}

function RiskBadge({ level }) {
  const styles = {
    high: 'bg-rose-50 text-rose-700 border-rose-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${styles[level] || styles.low}`}>{level || 'low'}</span>;
}
